import {
  OfflineGalleryFailedMessage,
  OfflineGalleryManifest,
  OfflineGalleryMetadata,
  OfflineGalleryReadyMessage,
  PrepareOfflineGalleryResult,
  ServiceWorkerInboundMessage,
  ServiceWorkerOutboundMessage,
  ServiceWorkerRequestMessage,
} from "./offline.types";

export const OFFLINE_GALLERY_STORAGE_KEY = "artetra.offline-gallery.public.v4";
export const SERVICE_WORKER_READY_TIMEOUT_MS = 10000;
export const SERVICE_WORKER_RESPONSE_TIMEOUT_MS = 300000;
export const ENABLE_OFFLINE_SW_IN_DEV_ENV = "REACT_APP_ENABLE_OFFLINE_SW_IN_DEV";
const OFFLINE_GALLERY_CACHE_VERSION = "v4";
const ACTIVE_MANIFEST_CACHE = `offline-gallery-active-${OFFLINE_GALLERY_CACHE_VERSION}`;
const ACTIVE_MANIFEST_URL = "/__artetra_offline_gallery_manifest__";
const LEGACY_OFFLINE_GALLERY_STORAGE_KEYS = [
  "artetra.offline-gallery.public.v3",
  "artetra.offline-gallery.public.v2",
  "artetra.offline-gallery.public.v1",
  "artetra.offline-gallery.personal.v1",
];

export const getOfflineGalleryStorageKey = () => OFFLINE_GALLERY_STORAGE_KEY;

const resolveWithTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  fallbackValue: T
): Promise<T> => {
  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => resolve(fallbackValue), timeoutMs);
    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch(() => {
        window.clearTimeout(timeoutId);
        resolve(fallbackValue);
      });
  });
};

const getServiceWorkerInstance = async (): Promise<ServiceWorker | null> => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  const registration = await resolveWithTimeout<ServiceWorkerRegistration | null>(
    navigator.serviceWorker.ready,
    SERVICE_WORKER_READY_TIMEOUT_MS,
    null
  );
  if (!registration) return null;
  return registration.active || registration.waiting || registration.installing || null;
};

const dedupeUrls = (urls: string[]): string[] => {
  return Array.from(new Set(urls.filter(Boolean)));
};

const resolveAbsoluteUrl = (url: string): string => {
  return new URL(url, window.location.origin).toString();
};

const isCacheableHttpUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch (_error) {
    return false;
  }
};

const resolveServiceWorkerUrl = (): string => {
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const workerPath = publicUrl ? `${publicUrl}/sw.js` : "/sw.js";
  return new URL(workerPath, window.location.origin).toString();
};

const isPublicGalleryRouteUrl = (value: string): boolean => {
  try {
    const url = new URL(value, window.location.origin);
    const mode = url.searchParams.get("t");
    return /^\/gallery\/[^/]+$/.test(url.pathname) && mode !== "personal" && mode !== "admin";
  } catch (_error) {
    return false;
  }
};

export const shouldRegisterOfflineServiceWorker = (
  nodeEnv = process.env.NODE_ENV,
  enableInDev = process.env.REACT_APP_ENABLE_OFFLINE_SW_IN_DEV
): boolean => {
  return nodeEnv === "production" || (nodeEnv === "development" && enableInDev === "true");
};

export const shouldCleanupDevelopmentOfflineServiceWorker = (
  nodeEnv = process.env.NODE_ENV,
  enableInDev = process.env.REACT_APP_ENABLE_OFFLINE_SW_IN_DEV
): boolean => {
  return nodeEnv === "development" && enableInDev !== "true";
};

const isOfflineGalleryCacheName = (cacheName: string): boolean => {
  return (
    cacheName.startsWith("offline-gallery-") ||
    /^app-shell-v\d+$/.test(cacheName)
  );
};

export const cleanupDevelopmentOfflineServiceWorker = async (): Promise<void> => {
  if (typeof window === "undefined") return;

  try {
    if ("serviceWorker" in navigator && typeof navigator.serviceWorker.getRegistrations === "function") {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ("caches" in window) {
      const cacheNames = await window.caches.keys();
      await Promise.all(
        cacheNames
          .filter(isOfflineGalleryCacheName)
          .map((cacheName) => window.caches.delete(cacheName))
      );
    }

    persistOfflineGalleryMetadata(null);
  } catch (error) {
    console.error("Failed to clean up local offline service worker state", error);
  }
};

export const registerOfflineServiceWorker = async (
  nodeEnv = process.env.NODE_ENV,
  enableInDev = process.env.REACT_APP_ENABLE_OFFLINE_SW_IN_DEV
): Promise<ServiceWorkerRegistration | null> => {
  if (
    typeof window === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return null;
  }

  if (!shouldRegisterOfflineServiceWorker(nodeEnv, enableInDev)) {
    if (shouldCleanupDevelopmentOfflineServiceWorker(nodeEnv, enableInDev)) {
      await cleanupDevelopmentOfflineServiceWorker();
    }
    return null;
  }

  try {
    const workerUrl = resolveServiceWorkerUrl();
    return await navigator.serviceWorker.register(workerUrl);
  } catch (error) {
    console.error("Failed to register offline service worker", error);
    return null;
  }
};

export const syncOfflineServiceWorkerClientContext = async (
  routeUrl = window.location.href
): Promise<void> => {
  const worker = await getServiceWorkerInstance();
  worker?.postMessage({
    type: "SET_OFFLINE_GALLERY_CLIENT_CONTEXT",
    publicGallery: isPublicGalleryRouteUrl(routeUrl),
  } satisfies ServiceWorkerOutboundMessage);
};

export const getOfflineGalleryMetadata = (): OfflineGalleryMetadata | null => {
  if (typeof window === "undefined") return null;

  try {
    const rawValue = window.localStorage.getItem(OFFLINE_GALLERY_STORAGE_KEY);
    if (!rawValue) return null;
    return JSON.parse(rawValue) as OfflineGalleryMetadata;
  } catch (error) {
    console.error("Failed to read offline gallery metadata", error);
    return null;
  }
};

export const persistOfflineGalleryMetadata = (metadata: OfflineGalleryMetadata | null) => {
  if (typeof window === "undefined") return;

  LEGACY_OFFLINE_GALLERY_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));

  if (!metadata) {
    window.localStorage.removeItem(OFFLINE_GALLERY_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(OFFLINE_GALLERY_STORAGE_KEY, JSON.stringify(metadata));
};

const toOfflineGalleryMetadata = (value: any): OfflineGalleryMetadata | null => {
  if (!value?.galleryId || !value?.routePath || !value?.preparedAt) return null;

  return {
    galleryId: String(value.galleryId),
    title: String(value.title || "Public gallery"),
    routePath: String(value.routePath),
    preparedAt: String(value.preparedAt),
    cacheVersion: value.cacheVersion ? String(value.cacheVersion) : OFFLINE_GALLERY_CACHE_VERSION,
  };
};

export const readActiveOfflineGalleryMetadata = async (): Promise<OfflineGalleryMetadata | null> => {
  if (typeof window === "undefined" || !("caches" in window)) return null;

  try {
    const activeManifestCache = await window.caches.open(ACTIVE_MANIFEST_CACHE);
    const manifestResponse = await activeManifestCache.match(ACTIVE_MANIFEST_URL);
    if (!manifestResponse) return null;
    return toOfflineGalleryMetadata(await manifestResponse.json());
  } catch (error) {
    console.error("Failed to read active offline gallery manifest", error);
    return null;
  }
};

export const syncOfflineGalleryMetadataFromCache = async (): Promise<OfflineGalleryMetadata | null> => {
  const activeMetadata = await readActiveOfflineGalleryMetadata();
  if (activeMetadata) {
    persistOfflineGalleryMetadata(activeMetadata);
    return activeMetadata;
  }

  return getOfflineGalleryMetadata();
};

export const collectAppShellUrls = (): string[] => {
  if (typeof window === "undefined") return [];

  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const defaultUrls = [
    resolveAbsoluteUrl(publicUrl ? `${publicUrl}/` : "/"),
    resolveAbsoluteUrl(publicUrl ? `${publicUrl}/index.html` : "/index.html"),
    resolveAbsoluteUrl(publicUrl ? `${publicUrl}/manifest.json` : "/manifest.json"),
    resolveAbsoluteUrl(publicUrl ? `${publicUrl}/favicon.ico` : "/favicon.ico"),
    resolveAbsoluteUrl(publicUrl ? `${publicUrl}/logo.png` : "/logo.png"),
    resolveAbsoluteUrl(publicUrl ? `${publicUrl}/logo.png` : "/logo.png"),
  ];

  const scriptUrls = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]")).map((node) => node.src);
  const stylesheetUrls = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href], link[rel="icon"][href], link[rel="manifest"][href]')
  ).map((node) => node.href);

  return dedupeUrls(
    [...defaultUrls, ...scriptUrls, ...stylesheetUrls]
      .filter(isCacheableHttpUrl)
      .map(resolveAbsoluteUrl)
  );
};

const buildPreparationFailure = (
  previousMetadata: OfflineGalleryMetadata | null,
  error: string
): PrepareOfflineGalleryResult => ({
  ok: false,
  previousMetadata,
  error,
});

const createServiceWorkerRequestId = (): string => {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoApi && typeof (cryptoApi as any).randomUUID === "function") {
    return (cryptoApi as any).randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const resolveOfflineGalleryPreparationResult = (
  previousMetadata: OfflineGalleryMetadata | null,
  manifest: OfflineGalleryManifest,
  response: ServiceWorkerInboundMessage | null
): PrepareOfflineGalleryResult => {
  if (!response) {
    return buildPreparationFailure(previousMetadata, "Offline service worker is not available.");
  }

  if (response.type === "OFFLINE_GALLERY_FAILED") {
    return buildPreparationFailure(previousMetadata, response.error || "Failed to prepare offline gallery.");
  }

  if (response.type !== "OFFLINE_GALLERY_READY" || response.galleryId !== manifest.galleryId) {
    return buildPreparationFailure(previousMetadata, "Offline service worker did not confirm this gallery.");
  }

  return {
    ok: true,
    metadata: {
      galleryId: manifest.galleryId,
      title: manifest.title,
      routePath: manifest.routePath,
      preparedAt: response.preparedAt || manifest.preparedAt,
      cacheVersion: response.cacheVersion,
    },
  };
};

const postMessageToServiceWorker = async (
  message: ServiceWorkerRequestMessage
): Promise<ServiceWorkerInboundMessage | null> => {
  const serviceWorker = await getServiceWorkerInstance();
  if (!serviceWorker) {
    return null;
  }

  if (typeof MessageChannel === "undefined") {
    return null;
  }

  return new Promise<ServiceWorkerInboundMessage | null>((resolve) => {
    const channel = new MessageChannel();
    const timeoutId = window.setTimeout(() => {
      channel.port1.onmessage = null;
      channel.port1.close();
      resolve(null);
    }, SERVICE_WORKER_RESPONSE_TIMEOUT_MS);

    channel.port1.onmessage = (event: MessageEvent<ServiceWorkerInboundMessage>) => {
      if (event.data?.requestId && event.data.requestId !== message.requestId) {
        return;
      }

      window.clearTimeout(timeoutId);
      channel.port1.onmessage = null;
      channel.port1.close();
      resolve(event.data);
    };

    serviceWorker.postMessage(message, [channel.port2]);
  });
};

export const prepareOfflineGallery = async (
  manifest: OfflineGalleryManifest
): Promise<PrepareOfflineGalleryResult> => {
  const previousMetadata = getOfflineGalleryMetadata();
  const requestId = createServiceWorkerRequestId();
  const response = await postMessageToServiceWorker({
    type: "PREPARE_OFFLINE_GALLERY",
    requestId,
    manifest: {
      ...manifest,
      appShellUrls: dedupeUrls([
        ...manifest.appShellUrls,
        ...collectAppShellUrls(),
      ]),
    },
  });

  const result = resolveOfflineGalleryPreparationResult(previousMetadata, manifest, response);
  if (result.ok) {
    persistOfflineGalleryMetadata(result.metadata);
  }
  return result;
};

export const clearOfflineGallery = async (): Promise<{ ok: true } | { ok: false; error: string }> => {
  const requestId = createServiceWorkerRequestId();
  const response = await postMessageToServiceWorker({ type: "CLEAR_OFFLINE_GALLERY", requestId });
  if (response?.type === "OFFLINE_GALLERY_CLEARED") {
    persistOfflineGalleryMetadata(null);
    return { ok: true };
  }

  return {
    ok: false,
    error: response?.type === "OFFLINE_GALLERY_FAILED"
      ? response.error
      : "Offline service worker did not confirm cache clearing.",
  };
};

export const getOfflineGalleryHref = (metadata: OfflineGalleryMetadata | null): string | null => {
  return metadata?.routePath || null;
};

export const getOfflineGalleryFailureMessage = (
  cachedGallery: OfflineGalleryMetadata | null,
  requestedGalleryId?: string
): string => {
  if (!cachedGallery) {
    return "This device does not have a public gallery prepared for offline viewing yet.";
  }

  if (requestedGalleryId && cachedGallery.galleryId !== requestedGalleryId) {
    return "This gallery is not available offline on this device. Only the last prepared public gallery can reopen without a network connection.";
  }

  return "This gallery could not be reopened from the offline cache.";
};

export const isOfflinePreparationFailure = (
  response: ServiceWorkerInboundMessage | null
): response is OfflineGalleryFailedMessage => {
  return Boolean(response && response.type === "OFFLINE_GALLERY_FAILED");
};

export const isOfflinePreparationSuccess = (
  response: ServiceWorkerInboundMessage | null
): response is OfflineGalleryReadyMessage => {
  return Boolean(response && response.type === "OFFLINE_GALLERY_READY");
};
